// @ts-check
import Markdown from "react-markdown"
import { Loader2, AlertCircle } from "lucide-react"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"
import { useReview } from "@/services/chat-provider/reviewsQuery"

/** Dispatch this with `{ detail: { reviewId } }` from anywhere to open the review panel. */
export const OPEN_REVIEW_PANEL_EVENT = "open-review-panel"

/**
 * Side panel that polls a review by id and renders the generated report once
 * it's ready.
 * @param {{ reviewId: string | null, open: boolean, onOpenChange: (open: boolean) => void }} props
 */
export default function ReviewPanel({ reviewId, open, onOpenChange }) {
  const { data: review, isLoading, isError } = useReview(open ? reviewId : null)

  const status = review?.status

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-xl w-full flex flex-col" data-testid="review-panel">
        <SheetHeader>
          <SheetTitle>Review report</SheetTitle>
          <SheetDescription>
            {status === "complete"
              ? "The AI-generated review is ready below."
              : "Your document has been sent for review."}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {(isLoading || status == null || status === "pending" || status === "processing") && !isError && (
            <div className="flex flex-col items-center justify-center gap-3 h-full text-sm text-muted-foreground">
              <Loader2 className="size-5 animate-spin" />
              <span>Generating review...</span>
            </div>
          )}

          {(isError || status === "failed") && (
            <div className="flex flex-col items-center justify-center gap-2 h-full text-sm text-destructive text-center">
              <AlertCircle className="size-5" />
              <span>{review?.error || "Something went wrong generating this review."}</span>
            </div>
          )}

          {status === "complete" && (
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <Markdown>{review.reportContent ?? ""}</Markdown>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
